import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'

import { useAuth } from '../AuthContext'
import { useSelectedHospital } from '../SelectedHospitalContext'
import { ApiError } from '../api/client'
import {
  compactAge,
  dashboardKey,
  fetchDashboard,
  type DashboardData,
  type DashboardTile,
} from '../api/dashboard'
import { AUDIT_LOG_KEY, fetchAuditLog, type AuditEntry } from '../api/auditLog'
import {
  fetchMyHospital,
  hospitalMeKey,
  type BedState,
  type HospitalMe,
} from '../api/hospitals'
import { EmergencyHelpUsageCard } from '../components/EmergencyHelpUsageCard'
import { StatCard } from '../components/StatCard'
import { TransportAttentionBanner } from '../components/TransportAttentionBanner'

/**
 * The Dashboard (M23-T05), drawn on `1181:4442`.
 *
 * Replaces the old home, which was one bordered card holding the hospital's
 * name and a five-row bed table. The bed table's editing controls already live
 * on `/admin/beds`; what stays here is a read-only Capacity panel, sourced from
 * the same endpoint the Beds page writes through so the two cannot disagree —
 * the frames' own numbers do (M23 defect D8: Capacity says Private `0 free/ 49`
 * while the Beds page says a total of `10`).
 */

const BED_ROWS: {
  label: string
  free: keyof BedState
  total: keyof BedState
}[] = [
  { label: 'ICU', free: 'icu_free', total: 'icu_total' },
  { label: 'General', free: 'general_free', total: 'general_total' },
  { label: 'Private', free: 'private_free', total: 'private_total' },
  { label: 'Executive', free: 'executive_free', total: 'executive_total' },
  { label: 'Maternity', free: 'maternity_free', total: 'maternity_total' },
]

/** All five wards. The frame draws three, and dropping two from the only
 *  screen that summarises them is a silent loss, not a simplification (gap B1). */
function Capacity({ beds }: { beds: BedState }) {
  return (
    <section
      aria-labelledby="capacity-heading"
      className="rounded-2xl border border-line-1 bg-bg-app p-6"
    >
      <h2
        id="capacity-heading"
        className="font-display text-lg font-semibold text-ink-1"
      >
        Capacity
      </h2>
      <hr className="mt-3 border-line-1" />

      <ul className="mt-4 flex flex-col gap-4">
        {BED_ROWS.map((row) => {
          const free = beds[row.free] as number
          const total = beds[row.total] as number
          const pct = total > 0 ? Math.round((free / total) * 100) : 0
          // The bar's colour follows the number it is drawn from, so a full
          // ward cannot read as an empty one. The frame paints every bar the
          // same dark red regardless of the count.
          const fill =
            free === 0
              ? 'bg-bed-bad'
              : pct <= 20
                ? 'bg-bed-warn'
                : 'bg-bed-good'
          return (
            <li key={row.label}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-ink-1">
                  {row.label} beds
                </span>
                <span className="text-sm tabular-nums text-ink-2">
                  {free} free / {total}
                </span>
              </div>
              <div className="mt-1.5 h-1 w-full rounded-full bg-bg-sunken">
                <div
                  className={`h-1 rounded-full ${fill}`}
                  style={{ width: `${total > 0 ? pct : 0}%` }}
                />
              </div>
            </li>
          )
        })}
      </ul>

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        <StatusDot
          ok={beds.ambulance_available}
          label={
            beds.ambulance_available ? 'Ambulance available' : 'No ambulance'
          }
        />
        {/* Three values, not two. `closed` is its own state and must not be
            folded into `divert` (gap B2): Emergency Help shows each as the
            hospital reported it. */}
        <StatusDot
          ok={beds.emergency_status === 'open'}
          label={
            beds.emergency_status === 'open'
              ? 'Emergency open'
              : beds.emergency_status === 'divert'
                ? 'Emergency on divert'
                : 'Emergency closed'
          }
        />
      </div>

      <Link
        to="/admin/beds"
        className="mt-5 inline-flex h-10 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-4 text-sm font-semibold text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Update inventory
      </Link>
      <p className="mt-2 text-xs text-ink-3">
        Updated {new Date(beds.updated_at).toLocaleString()}
      </p>
    </section>
  )
}

function StatusDot({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-ink-1">
      <span
        aria-hidden="true"
        className={`h-2 w-2 rounded-full ${ok ? 'bg-bed-good' : 'bg-bed-bad'}`}
      />
      {label}
    </span>
  )
}

/** `bed_inventory.update` → "Bed inventory updated". */
function actionSentence(action: string): string {
  const [subject = '', verb = ''] = action.split('.')
  const words = subject.replace(/_/g, ' ')
  const past = verb.endsWith('e') ? `${verb}d` : `${verb}ed`
  return `${words.charAt(0).toUpperCase()}${words.slice(1)} ${past}`
}

/** What actually changed, from before/after — not an invented label. */
function changeSummary(entry: AuditEntry): string {
  const keys = Object.keys(entry.after ?? {})
  const parts = keys
    .filter((k) => entry.before?.[k] !== entry.after?.[k])
    .slice(0, 2)
    .map(
      (k) =>
        `${k.replace(/_/g, ' ')} ${entry.before?.[k] ?? '—'} → ${entry.after?.[k]}`,
    )
  return parts.length > 0 ? parts.join(', ') : entry.target_table
}

function RecentActivity({
  entries,
  isPending,
  isError,
  onRetry,
}: {
  entries: AuditEntry[]
  isPending: boolean
  isError: boolean
  onRetry: () => void
}) {
  return (
    <section
      aria-labelledby="activity-heading"
      className="rounded-2xl border border-line-1 bg-bg-app p-6"
    >
      <header className="flex items-center justify-between gap-4">
        <h2
          id="activity-heading"
          className="font-display text-lg font-semibold text-ink-1"
        >
          Recent activity
        </h2>
        <span className="rounded-full border border-line-1 px-2.5 py-1 text-xs text-ink-3">
          Every action is logged
        </span>
      </header>

      {isPending && (
        <div
          role="status"
          aria-label="Loading recent activity"
          className="mt-4"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="mb-3 h-4 w-full animate-pulse rounded bg-bg-sunken"
            />
          ))}
        </div>
      )}

      {isError && (
        <div
          role="alert"
          className="mt-4 flex items-center justify-between gap-4"
        >
          <p className="text-sm text-bed-bad">
            Could not load recent activity.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex h-8 items-center rounded-[8px] border border-line-2 px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft"
          >
            Retry
          </button>
        </div>
      )}

      {!isPending && !isError && entries.length === 0 && (
        <p className="mt-6 text-sm text-ink-3">
          Nothing has been done here yet. Actions appear as they happen.
        </p>
      )}

      {!isPending && !isError && entries.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">Recent admin activity</caption>
            <thead>
              <tr className="bg-bg-sunken text-left">
                <th
                  scope="col"
                  className="rounded-l-lg px-3 py-2 font-medium text-ink-2"
                >
                  When
                </th>
                <th scope="col" className="px-3 py-2 font-medium text-ink-2">
                  Who
                </th>
                <th scope="col" className="px-3 py-2 font-medium text-ink-2">
                  Action
                </th>
                <th
                  scope="col"
                  className="rounded-r-lg px-3 py-2 font-medium text-ink-2"
                >
                  What changed
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-line-1 align-top">
                  <td className="px-3 py-3 text-ink-3">
                    <span title={new Date(e.occurred_at).toLocaleString()}>
                      {compactAge(e.occurred_at)} ago
                    </span>
                  </td>
                  <td className="px-3 py-3 capitalize text-ink-2">
                    {e.actor.role.replace(/_/g, ' ')}
                  </td>
                  <td className="px-3 py-3">
                    <span className="block text-ink-1">
                      {actionSentence(e.action)}
                    </span>
                    {/* Same event, same rendering as the Audit log page. */}
                    <span className="mt-1 inline-block rounded bg-bg-sunken px-1.5 py-0.5 font-mono text-xs text-ink-2">
                      {e.action}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-ink-2">{changeSummary(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function Tiles({ data }: { data: DashboardData }) {
  const tile = (t: DashboardTile, note: string) => ({
    count: t.count,
    ...(t.oldest_created_at !== null && {
      age: compactAge(t.oldest_created_at),
      note,
    }),
  })
  return (
    // Three tiles for a hospital admin, four for AMTC — the row re-flows rather
    // than leaving a hole where a tile they cannot use would have been.
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard
        label="Appointment requests"
        {...tile(data.appointment_requests, 'oldest waiting')}
      />
      <StatCard
        label="Diagnostics payment"
        {...tile(data.diagnostics_payment, 'oldest not yet secured')}
      />
      <StatCard
        label="Medication requests"
        {...tile(data.medication_requests, 'oldest no offer sent')}
      />
      {data.enquiries_and_leads !== null && (
        <StatCard
          label="Enquiries & leads"
          {...tile(data.enquiries_and_leads, 'oldest waiting on a callback')}
        />
      )}
    </div>
  )
}

function TilesSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading today's counts"
      className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
    >
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-32 animate-pulse rounded-2xl bg-bg-sunken" />
      ))}
    </div>
  )
}

export function AdminHomePage() {
  const { token, user } = useAuth()
  const { hospitalId } = useSelectedHospital()
  const isAmtc = user?.role === 'amtc_admin'
  const effectiveId = isAmtc ? hospitalId : null
  const ready = Boolean(token) && (!isAmtc || Boolean(hospitalId))

  const hospital = useQuery({
    queryKey: hospitalMeKey(effectiveId),
    queryFn: () => fetchMyHospital(token, effectiveId),
    enabled: ready,
  })

  const dashboard = useQuery({
    queryKey: dashboardKey(effectiveId),
    queryFn: () => fetchDashboard(token, effectiveId),
    enabled: Boolean(token),
  })

  const activity = useQuery({
    queryKey: [...AUDIT_LOG_KEY, 'recent', effectiveId],
    queryFn: () => fetchAuditLog(token, null, effectiveId),
    enabled: Boolean(token),
  })

  // Preserved from the old home: an amtc_admin oversees ten hospitals, so
  // "which one?" has to be answered before a hospital-shaped panel means
  // anything. Do NOT let this regress into a blank page.
  if (isAmtc && !hospitalId) {
    return (
      <>
        <PageHeading hospitalName={null} />
        <TransportAttentionBanner />
        {dashboard.isSuccess && <Tiles data={dashboard.data} />}
        {/* The same sentence as the Beds empty state, and true in both
            sidebar states for the same reason (M24-T06). */}
        <p className="mt-8 text-sm text-ink-3">
          Pick a hospital from the{' '}
          <strong className="text-ink-1">Managing</strong> selector in the
          sidebar to see its capacity. Expand the sidebar if it is collapsed.
        </p>
        <div className="mt-6">
          <EmergencyHelpUsageCard />
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeading hospitalName={hospital.data?.hospital.name ?? null} />
      {/* M40-T07: network-wide, so not scoped to the hospital picked. */}
      <TransportAttentionBanner />

      {dashboard.isPending && <TilesSkeleton />}
      {dashboard.isError && (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-2xl border border-bed-bad/40 bg-bed-bad-bg px-5 py-4"
        >
          <p className="text-sm font-medium text-bed-bad">
            {dashboard.error instanceof ApiError
              ? dashboard.error.message
              : "Could not load today's counts."}
          </p>
          <button
            type="button"
            onClick={() => void dashboard.refetch()}
            className="inline-flex h-9 items-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft"
          >
            Retry
          </button>
        </div>
      )}
      {dashboard.isSuccess && <Tiles data={dashboard.data} />}

      {/* `min-w-0` on the children, not just `minmax(0,…)` on the template.
          The template only applies at `lg`; below it this is an implicit single
          column, where a grid item still defaults to `min-width: auto` and the
          activity table's min-content width pushes the whole PAGE sideways —
          458px of document in a 390px viewport, measured (M23-T15). */}
      <div className="mt-6 grid gap-6 [&>*]:min-w-0 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <RecentActivity
          entries={activity.data?.data ?? []}
          isPending={activity.isPending}
          isError={activity.isError}
          onRetry={() => void activity.refetch()}
        />
        <CapacityRegion query={hospital} />
      </div>
      {/* AMTC only (M35-T07): the endpoint is amtc_admin-only, and a hospital
          admin has no use for network-wide emergency counts. */}
      {isAmtc && (
        <div className="mt-6">
          <EmergencyHelpUsageCard />
        </div>
      )}
    </>
  )
}

function PageHeading({ hospitalName }: { hospitalName: string | null }) {
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
  return (
    <header className="mb-6">
      <h1 className="font-display text-2xl font-semibold text-ink-1">Today</h1>
      <p className="mt-1 text-sm text-ink-3">
        {today}
        {hospitalName !== null && ` · ${hospitalName}`}
      </p>
    </header>
  )
}

function CapacityRegion({
  query,
}: {
  query: {
    isPending: boolean
    isError: boolean
    error: unknown
    data: HospitalMe | undefined
    refetch: () => unknown
  }
}) {
  if (query.isPending) {
    return (
      <div
        role="status"
        aria-label="Loading capacity"
        className="h-72 animate-pulse rounded-2xl bg-bg-sunken"
      />
    )
  }
  if (query.isError) {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-4 rounded-2xl border border-bed-bad/40 bg-bed-bad-bg px-5 py-4"
      >
        <p className="text-sm font-medium text-bed-bad">
          {query.error instanceof ApiError
            ? query.error.message
            : 'Could not load your hospital.'}
        </p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-9 items-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft"
        >
          Retry
        </button>
      </div>
    )
  }
  if (query.data === undefined) return null
  return <Capacity beds={query.data.beds} />
}
